import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { assetModelPrecision } from 'utils/constants'
import { parseNumber, parseString } from 'utils/parsingHelpers'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export interface GeckoAnnularRingWeldLineJSON {
  type: GeckoFeatureModelType.AnnularRingWeldLine
  name: string
  feature_geometry: {
    // Angular direction in degrees, counterclockwise from the positive x-axis of the floor
    angle: number
  }
}

export const NewGeckoAnnularRingWeldLineJSON: GeckoAnnularRingWeldLineJSON = {
  type: GeckoFeatureModelType.AnnularRingWeldLine,
  name: 'New Weld Line',
  feature_geometry: {
    angle: 0,
  },
}

/**
 * A weld line divides the annular ring region of a tank floor into its individual
 * annular plates (courses). It is defined only by its angular direction, and is displayed
 * as a radial line between the annular ring and the border of the floor.
 */
export class GeckoAnnularRingWeldLine extends GeckoEditableFeature {
  public readonly name: string
  public readonly type = GeckoFeatureModelType.AnnularRingWeldLine
  public readonly angle: number

  private constructor(id: string, name: string, angle: number) {
    super(id)
    this.name = name
    this.angle = angle
  }

  public static CreateFromJSON(
    componentID: string,
    weldLineID: string,
    weldLineJSON: GeckoAnnularRingWeldLineJSON
  ): GeckoAnnularRingWeldLine {
    const name = parseString(weldLineJSON.name) ?? 'Weld Line'
    const angle = parseNumber(weldLineJSON.feature_geometry?.angle) ?? 0
    return new GeckoAnnularRingWeldLine(weldLineID, name, angle)
  }

  public static CreateMapFromJSON(
    componentID: string,
    weldLinesJSON: Record<string, GeckoAnnularRingWeldLineJSON>
  ): Map<string, GeckoAnnularRingWeldLine> {
    const result = new Map<string, GeckoAnnularRingWeldLine>()
    Object.entries(weldLinesJSON).forEach(([weldLineID, weldLineJSON]) => {
      result.set(
        weldLineID,
        GeckoAnnularRingWeldLine.CreateFromJSON(
          componentID,
          weldLineID,
          weldLineJSON
        )
      )
    })
    return result
  }

  public asJSON(): GeckoAnnularRingWeldLineJSON {
    return generateAnnularRingWeldLine(this.name, this.angle)
  }
}

export function generateAnnularRingWeldLine(
  name: string,
  angle: number
): GeckoAnnularRingWeldLineJSON {
  return {
    type: GeckoFeatureModelType.AnnularRingWeldLine,
    name: name,
    feature_geometry: {
      angle: Number(angle.toPrecision(assetModelPrecision)),
    },
  }
}

export const AnnularRingWeldLineFormFields = (
  weldLineJSON: GeckoAnnularRingWeldLineJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Line Name',
      value: weldLineJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'angle',
      label: 'Angle',
      value: weldLineJSON.feature_geometry.angle,
      unit_magnitude: UnitMagnitude.Degrees,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const AnnularRingWeldLineJSONFromFormFields = (
  formFields: FormField[]
) => {
  const name = getFormFieldByDataKey(formFields, 'name')?.value ?? ''
  const angle = getFormFieldByDataKey(formFields, 'angle')?.value ?? 0

  return generateAnnularRingWeldLine(name as string, angle as number)
}

export const AnnularRingWeldLineFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.AnnularRingWeldLine,
  title: 'Annular Ring Weld Lines',
  singularName: 'Annular Ring Weld Line',
  fields: ['name', 'angle'],
  sortBy: [{ fieldName: 'angle', order: 'asc' }],
}
